/**
 * DomainEnforcer
 * 
 * Reactive cross-domain redirect when user's locale/location
 * no longer matches the current domain's brand scope.
 * 
 * Contract:
 * - tributario.com.br = Brazil + PT locale
 * - tax.network = everything else
 * - Dev domains: no redirects
 * 
 * Guards (skip redirect when):
 * - session_token present in URL (cross-domain handshake in progress)
 * - Current path is a functional route (account, auth, plans, checkout)
 * - Current entity is an expert profile (locale-agnostic)
 * 
 * Uses appendCrossDomainToken pattern from DeterministicRouter
 * for authenticated session transfer.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useUserContext } from '@/contexts/UserContext';
import { useRouteEntitySafe } from '@/contexts/RouteEntityContext';
import { DOMAIN_DEFAULTS } from '@/lib/domainDefaults';
import { FUNCTIONAL_ROUTE_IDS } from '@/constants/routing';
import { isPTLocale, BRAZIL_LOCATION_ID, TAX_NETWORK_DOMAIN, TRIBUTARIO_DOMAIN } from '@/constants/branding';
import { redirectWithSession } from '@/lib/crossDomainRedirect';
import { isCrossDomainHandshakeActive } from '@/lib/crossDomainHandshake';
import { isDomainRedirectArmed, disarmDomainRedirect } from '@/lib/domainRedirectArm';
import {
  CROSS_DOMAIN_LOCALE_PARAM,
  CROSS_DOMAIN_LOCATION_PARAM,
  getCrossDomainLocaleOverride,
  getCrossDomainLocationOverride,
} from '@/lib/crossDomainState';
import { logger } from '@/utils/logger';

/** Extract the first path segment (without leading slash) */
function getFirstSegment(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] || '';
}

export function DomainEnforcer(): null {
  const { settings, isLoading: localeLoading } = useLocale();
  const { currentLocation, isLoading: locationLoading, locationSource } = useUserContext();
  const routeEntity = useRouteEntitySafe();
  const location = useLocation();
  const redirectingRef = useRef(false);

  useEffect(() => {
    // Skip on dev domains — no cross-domain enforcement
    if (DOMAIN_DEFAULTS.isDev) return;
    // Skip if already redirecting
    if (redirectingRef.current) return;
    // Wait for both contexts to finish initializing before evaluating
    if (localeLoading || locationLoading) return;
    // Skip during initial load (no location yet)
    if (!currentLocation || !settings.locale) return;

    // Guard 1: Skip while cross-domain session handshake is in progress
    const params = new URLSearchParams(window.location.search);
    if (
      params.has('session_token') ||
      params.has(CROSS_DOMAIN_LOCALE_PARAM) ||
      params.has(CROSS_DOMAIN_LOCATION_PARAM) ||
      isCrossDomainHandshakeActive()
    ) {
      logger.debug('[DomainEnforcer] Handshake in progress, skipping');
      return;
    }

    // Guard 2: Skip if there are unconsumed cross-domain overrides in sessionStorage
    if (getCrossDomainLocaleOverride() || getCrossDomainLocationOverride()) {
      logger.debug('[DomainEnforcer] Unconsumed cross-domain overrides, skipping');
      return;
    }

    // Guard 3: Skip for functional routes (account, auth, plans, checkout, admin)
    const firstSegment = getFirstSegment(location.pathname);
    if (FUNCTIONAL_ROUTE_IDS.has(firstSegment)) {
      logger.debug('[DomainEnforcer] Functional route, skipping', { segment: firstSegment });
      return;
    }

    // Guard 4: Skip for expert profiles (locale-agnostic)
    if (routeEntity?.currentEntity?.type === 'expert') {
      logger.debug('[DomainEnforcer] Expert profile route, skipping');
      return;
    }

    // Guard 5: Only redirect on explicit user action OR persisted user_selection
    // isDomainRedirectArmed() = user just clicked a selector in this session
    // locationSource === 'user_selection' = user previously explicitly chose this location
    // This prevents DB-driven (account) or IP-driven init from triggering redirects,
    // but honors a previous explicit choice when revisiting the domain.
    const isExplicitUserChoice = isDomainRedirectArmed() || locationSource === 'user_selection';
    if (!isExplicitUserChoice) {
      return;
    }

    const isPT = isPTLocale(settings.locale);
    const isBrazil = currentLocation === BRAZIL_LOCATION_ID;
    const isTributarioBrand = isPT && isBrazil;

    if (DOMAIN_DEFAULTS.isTributario && !isTributarioBrand) {
      // On tributario.com.br but user moved away from Brazil+PT → go to tax.network
      redirectingRef.current = true;
      disarmDomainRedirect();
      const path = window.location.pathname + window.location.search + window.location.hash;
      logger.info('[DomainEnforcer] User left Brazil+PT on tributario, redirecting to tax.network');
      redirectWithSession(`https://${TAX_NETWORK_DOMAIN}${path}`, {
        locale: settings.locale,
        locationId: currentLocation,
      });
    } else if (DOMAIN_DEFAULTS.isTaxNetwork && isTributarioBrand) {
      // On tax.network but user selected Brazil+PT → go to tributario.com.br
      redirectingRef.current = true;
      disarmDomainRedirect();
      const path = window.location.pathname + window.location.search + window.location.hash;
      logger.info('[DomainEnforcer] User selected Brazil+PT on tax.network, redirecting to tributario');
      redirectWithSession(`https://${TRIBUTARIO_DOMAIN}${path}`, {
        locale: settings.locale,
        locationId: currentLocation,
      });
    } else {
      // User changed locale/location but still matches current domain — disarm
      disarmDomainRedirect();
    }
  }, [settings.locale, currentLocation, locationSource, localeLoading, locationLoading, location.pathname, routeEntity?.currentEntity?.type]);

  return null;
}
